import React, { memo, useMemo } from "react";
import { Animated, Pressable, Text, View } from "react-native";
import type { TabItemConfig } from "./Tabs";
import { TabTriggerSlotProps } from "expo-router/ui";

import { setActiveTabName, useActiveTab } from "./useActiveTab";
import { useTranslation } from "../localization";

const DEFAULT_COLORS = {
  primary: "#6200ee",
  onSurfaceVariant: "#999999",
  secondary: "#03dac6",
  onSecondary: "#000000",
  surface: "#1e1e1e",
};

type AnimatedTabButtonProps = TabTriggerSlotProps & {
  tab: TabItemConfig;
  translateTitles?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cfg?: Record<string, any>;
  ns?: string;
  onTabChange?: (prev: string | undefined, next: string) => void;
};

const AnimatedTabButton = ({
  tab,
  isFocused,
  onPress,
  onPressIn,
  onPressOut,
  translateTitles = true,
  ns,
  onTabChange,
  cfg = {},
  ...rest
}: AnimatedTabButtonProps) => {
  const { t } = useTranslation(ns);
  const activeTab = useActiveTab();
  const focused = useMemo(() => !!isFocused, [isFocused]);
  const [scaleAnim] = React.useState(() => new Animated.Value(1));
  const [glowAnim] = React.useState(() => new Animated.Value(0));
  const displayTitle = useMemo(
    () => (translateTitles ? t(tab.title || tab.name) : tab.title || tab.name),
    [tab.title, tab.name, t, translateTitles],
  );
  const currentColors = useMemo(
    () => ({
      iconColor: focused
        ? cfg.colors?.activeTint || DEFAULT_COLORS.primary
        : cfg.colors?.inactiveTint || DEFAULT_COLORS.onSurfaceVariant,
      textColor: focused
        ? cfg.colors?.activeTint || DEFAULT_COLORS.primary
        : cfg.colors?.inactiveTint || DEFAULT_COLORS.onSurfaceVariant,
    }),
    [cfg.colors?.activeTint, cfg.colors?.inactiveTint, focused],
  );

  React.useEffect(() => {
    Animated.spring(scaleAnim, {
      toValue: focused ? 1.08 : 1,
      useNativeDriver: true,
      tension: 200,
      friction: 10,
    }).start();
    Animated.timing(glowAnim, {
      toValue: focused ? 1 : 0,
      duration: 250,
      useNativeDriver: true,
    }).start();
  }, [focused, glowAnim, scaleAnim]);

  return (
    <Pressable
      onPress={(e) => {
        onPress?.(e);
        setActiveTabName(tab.name);
        onTabChange?.(activeTab, tab.name);
      }}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={{ flex: 1 }}
      accessibilityRole="tab"
      accessibilityState={{ selected: focused }}
      {...rest}
    >
      <Animated.View style={cfg.itemStyle}>
        <Animated.View
          style={[
            {
              opacity: glowAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 0.15],
              }),
            },
            ...cfg?.glowItemStyle,
          ]}
        />
        {tab.tabBarIcon && (
          <View
            style={{
              marginBottom: 2,
            }}
          >
            {tab.tabBarIcon({
              focused,
              color: currentColors.iconColor,
              size: 24,
            })}
          </View>
        )}
        <Text
          style={[
            {
              fontSize: cfg.compact ? 9 : 10,
              fontWeight: "700",
              letterSpacing: 0.6,
              textTransform: "uppercase" as const,
              marginTop: 1,
              color: currentColors.textColor,
              textAlign: "center",
              maxWidth: "100%",
            },
            cfg.labelStyle,
          ]}
          numberOfLines={1}
        >
          {displayTitle}
        </Text>
        {tab.badge && (
          <View
            style={{
              position: "relative",
              top: -2,
              right: 2,
              backgroundColor:
                tab.badgeColor ||
                cfg.colors?.secondary ||
                DEFAULT_COLORS.secondary,
              borderRadius: 10,
              minWidth: 18,
              height: 18,
              justifyContent: "center",
              alignItems: "center",
              paddingHorizontal: 5,
              borderWidth: 2,
              borderColor: cfg.colors?.background || DEFAULT_COLORS.surface,
              shadowColor:
                tab.badgeColor ||
                cfg.colors?.secondary ||
                DEFAULT_COLORS.secondary,
              shadowOpacity: 0.3,
              shadowRadius: 3,
              shadowOffset: { height: 1, width: 0 },
              elevation: 3,
            }}
          >
            <Text
              style={{
                color:
                  tab.badgeTextColor ||
                  cfg.colors?.onSecondary ||
                  DEFAULT_COLORS.onSecondary,
                fontSize: 8,
                fontWeight: "800",
                textAlign: "center",
              }}
            >
              {typeof tab.badge === "number" && tab.badge > 99
                ? "99+"
                : tab.badge}
            </Text>
          </View>
        )}
      </Animated.View>
    </Pressable>
  );
};
export default memo(AnimatedTabButton);
